import { List, ListItem, Stack, Typography } from '@mui/material'
import { useMemo, type PropsWithChildren } from 'react'

import { CodeBlock } from 'components/common/CodeBlock'
import { AuthenticationStep } from 'components/common/mapped-entities/AuthenticationStep'
import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { type GcpIntegrationSetupResponse } from 'types/api/gcp/integrations'

function CodeList({ list }: { list: string[] }) {
  return <CodeBlock code={list.join('\n')} includeLineNumbers={false} />
}

function GcpExternalLink({ href, title }: { href: string; title: string }) {
  return (
    <MUIExternalLink href={href}>
      {title}
      <Icon
        css={{
          height: muiTheme.spacing(2),
          marginLeft: muiTheme.spacing(0.5),
          width: muiTheme.spacing(2)
        }}
        icon="arrowUpRightFromSquare"
      />
    </MUIExternalLink>
  )
}

function ProductEnablementLink({
  href,
  title
}: {
  href: string
  title: string
}) {
  return (
    <ListItem disablePadding>
      <GcpExternalLink href={href} title={title} />
    </ListItem>
  )
}

/**
 * children will likely be a form context wrapping the necessary auth fields in a form controller
 */
export function GcpAuthenticationSection({
  children,
  gremlinGcpProxyServiceAccount,
  principalAccessRoles,
  serviceAccountOutOfBoxRoles
}: PropsWithChildren<GcpIntegrationSetupResponse>) {
  const sortedServiceAccountOutOfBoxRoles = useMemo(
    () => [...serviceAccountOutOfBoxRoles].sort(),
    [serviceAccountOutOfBoxRoles]
  )

  return (
    <Stack sx={{ rowGap: 2, width: '100%' }}>
      <Typography sx={{ marginTop: 2 }}>
        In the GCP Console, navigate to your preferred project before following
        the next steps.
      </Typography>
      <List disablePadding>
        <AuthenticationStep
          badgeNumber={1}
          title={
            <Typography variant="inherit">
              Under{' '}
              <GcpExternalLink
                href="https://console.cloud.google.com/iam-admin/serviceaccounts"
                title="Service Accounts"
              />
              , create a new service account
            </Typography>
          }
        >
          <Typography>
            We recommended calling it <code>gremlin-access</code>. Grant the{' '}
            <code>{gremlinGcpProxyServiceAccount}</code> principal access with
            the following roles:
          </Typography>
          <CodeList list={principalAccessRoles} />
        </AuthenticationStep>
        <AuthenticationStep
          badgeNumber={2}
          title={
            <Typography variant="inherit">
              Under{' '}
              <GcpExternalLink
                href="https://console.cloud.google.com/iam-admin/iam"
                title="IAM"
              />
              , configure project access
            </Typography>
          }
        >
          <Typography>
            Grant access for the <code>gremlin-access</code> service account,
            assigning it the following roles:
          </Typography>
          <CodeList list={sortedServiceAccountOutOfBoxRoles} />
        </AuthenticationStep>
        <AuthenticationStep badgeNumber={3} title="Enable product APIs">
          <Typography>
            To enable, visit the pages below. Make sure to select the correct
            Google Project before enabling.
          </Typography>
          <List disablePadding>
            <ProductEnablementLink
              href="https://console.cloud.google.com/marketplace/product/google/compute.googleapis.com?q=search&referrer=search"
              title="Compute Admin API"
            />
            <ProductEnablementLink
              href="https://console.cloud.google.com/marketplace/product/google/monitoring.googleapis.com?q=search&referrer=search"
              title="Monitoring API"
            />
            <ProductEnablementLink
              href="https://console.cloud.google.com/marketplace/product/google/run.googleapis.com?q=search&referrer=search"
              title="Cloud Run Admin API"
            />
          </List>
        </AuthenticationStep>
        <AuthenticationStep
          badgeNumber={4}
          title="Paste gremlin-access service account's email"
        >
          {children}
        </AuthenticationStep>
      </List>
    </Stack>
  )
}
